import React, { useEffect, useState } from 'react';

// URL base da API - usa variavel de ambiente ou relativa
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface SystemStatus {
  whatsapp_connected: boolean;
  ai_model: string;
  ai_status: string;
  backend_uptime: string;
  messages_today: number;
  quotes_today: number;
  last_message?: string;
  last_response?: string;
}

interface Message {
  client_phone: string;
  message: string;
  ai_response: string;
  timestamp: string;
  message_id: string;
}

interface Client {
  phone: string;
  name?: string;
  vehicle_info?: string;
  updated_at: string;
}

export default function ConectarWhatsapp() {
  const [qr, setQr] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [clients, setClients] = useState<Client[]>([]);

  const fetchQr = async () => {
    setLoading(true);
    setError(null);
    try {
      // Usar endpoint da nova API de conexao WhatsApp
      const res = await fetch(`${API_BASE_URL}/api/whatsapp/connection/qr`);
      if (!res.ok) throw new Error('QR Code nao disponivel');
      const data = await res.json();
      setQr(data.qr);
      setIsConnected(data.success && !data.qr);
    } catch (e: any) {
      setError(e.message);
      setQr(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/whatsapp/connection/status`);
      if (res.ok) {
        const data = await res.json();
        setSystemStatus({
          whatsapp_connected: data.status === 'connected',
          ai_model: 'Groq LLaMA',
          ai_status: 'online',
          backend_uptime: 'N/A',
          messages_today: 0,
          quotes_today: 0,
          last_message: undefined,
          last_response: undefined
        });
        setIsConnected(data.status === 'connected');
        if (data.qr) {
          setQr(data.qr);
        }
      }
    } catch (e) {
      console.error('Erro ao buscar status:', e);
    }
  };

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (e) {
      console.error('Erro ao buscar mensagens:', e);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clients`);
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (e) {
      console.error('Erro ao buscar clientes:', e);
    }
  };

  useEffect(() => {
    // Carregar dados iniciais
    fetchQr();
    fetchSystemStatus();
    fetchMessages();
    fetchClients();
    
    // Intervalos muito mais longos para reduzir requisições
    const qrInterval = setInterval(fetchQr, 30000); // 30 segundos
    const statusInterval = setInterval(fetchSystemStatus, 30000); // 30 segundos
    const messagesInterval = setInterval(fetchMessages, 60000); // 1 minuto
    
    return () => {
      clearInterval(qrInterval);
      clearInterval(statusInterval);
      clearInterval(messagesInterval);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-600';
      case 'offline': return 'text-red-600';
      case 'error': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return '🟢';
      case 'offline': return '🔴';
      case 'error': return '🟡';
      default: return '⚪';
    }
  };

  const formatPhone = (phone: string) => {
    // Remove @c.us e formata o número
    return phone.replace('@c.us', '').replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '($1) $2 $3-$4');
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900 mb-2">Painel Administrativo 7Ases</h1>
          <p className="text-blue-700">Monitoramento do Agente AI e WhatsApp</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* QR Code Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900">Conexão WhatsApp</h2>
            {loading && <div className="text-gray-500">Carregando QR Code...</div>}
            {error && <div className="text-red-500 mb-4">{error}</div>}
            {qr && !isConnected && (
              <div className="text-center">
                <img src={qr} alt="QR Code WhatsApp" className="w-64 h-64 mx-auto mb-4" />
                <p className="text-sm text-gray-600">Escaneie o QR Code acima com o WhatsApp da 7Ases</p>
              </div>
            )}
            {isConnected && (
              <div className="text-center">
                <div className="text-green-600 font-bold text-lg mb-2">✅ WhatsApp Conectado!</div>
                <p className="text-sm text-gray-600">Agente AI ativo e pronto para atendimento</p>
              </div>
            )}
            <div className="mt-4 text-xs text-gray-400">
              <p>1. Abra o WhatsApp no celular da 7Ases</p>
              <p>2. Vá em <b>Configurações &gt; Dispositivos vinculados</b></p>
              <p>3. Toque em <b>Vincular um dispositivo</b></p>
              <p>4. Escaneie o QR Code acima</p>
            </div>
          </div>

          {/* System Status Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900">Status do Sistema</h2>
            {systemStatus && (
              <div className="space-y-4">
                {/* WhatsApp Status */}
                <div className="border-b pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800">
                      <span className={`font-bold ${getStatusColor(systemStatus.whatsapp_connected ? 'online' : 'offline')}`}>
                        {getStatusIcon(systemStatus.whatsapp_connected ? 'online' : 'offline')} 
                        {systemStatus.whatsapp_connected ? 'Conectado' : 'Desconectado'}
                      </span>
                    </h3>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Mensagens hoje: {systemStatus.messages_today}</p>
                    {systemStatus.last_message && (
                      <p>Última mensagem: "{systemStatus.last_message.substring(0, 50)}..."</p>
                    )}
                  </div>
                </div>

                {/* AI Agent Status */}
                <div className="border-b pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800">
                      <span className={`font-bold ${getStatusColor(systemStatus.ai_status)}`}>
                        {getStatusIcon(systemStatus.ai_status)} 
                        {systemStatus.ai_status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </h3>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Modelo: {systemStatus.ai_model}</p>
                    <p>Cotações hoje: {systemStatus.quotes_today}</p>
                    {systemStatus.last_response && (
                      <p>Última resposta: "{systemStatus.last_response.substring(0, 50)}..."</p>
                    )}
                  </div>
                </div>

                {/* Backend Status */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-800">
                      <span className={`font-bold ${getStatusColor('online')}`}>
                        {getStatusIcon('online')} 
                        Online
                      </span>
                    </h3>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Uptime: {systemStatus.backend_uptime}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Messages */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900">Mensagens Recentes</h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {messages.map((msg, index) => (
                <div key={index} className="border-b pb-2">
                  <div className="flex justify-between items-start mb-1">
                    <span className="text-sm font-medium text-gray-800">
                      {formatPhone(msg.client_phone)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(msg.timestamp)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">
                    <strong>Cliente:</strong> "{msg.message.substring(0, 50)}..."
                  </p>
                  <p className="text-xs text-blue-600">
                    <strong>AI:</strong>{msg.ai_response.substring(0, 50)}..."
                  </p>
                </div>
              ))}
              {messages.length === 0 && (
                <p className="text-gray-500 text-sm">Nenhuma mensagem recente</p>
              )}
            </div>
          </div>
        </div>

        {/* Clients Section */}
        <div className="mt-6 bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-blue-900 mb-4">Clientes Atendidos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client, index) => (
              <div key={index} className="border rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-800">
                      {client.name || 'Cliente'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {formatPhone(client.phone)}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(client.updated_at)}
                  </span>
                </div>
                {client.vehicle_info && (
                  <p className="text-xs text-gray-600 mt-2">
                    <strong>Veículo:</strong> {client.vehicle_info}
                  </p>
                )}
              </div>
            ))}
            {clients.length === 0 && (
              <p className="text-gray-500 text-sm">Nenhum cliente registrado</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-blue-900 mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button 
              onClick={fetchQr}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              🔄 Atualizar QR Code
            </button>
            <button
              onClick={fetchSystemStatus}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              📊 Atualizar Status
            </button>
            <button 
              onClick={fetchMessages}
              className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              💬 Ver Mensagens
            </button>
            <a 
              href="/"
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-center"
            >
              🏠 Voltar ao Site
            </a>
          </div>
        </div>
      </div>
    </div>
  );
} 